import { FC } from 'react';
import { Popover, Spin } from 'antd';
import { useTrans } from 'base/utils/i18n';
import { Image } from '@base/lib/ThemeImage';
import { getWindowWidth, getWindowHeight } from 'ava-util/dist/dom-window';
import cn from 'classnames';
import styles from './index.module.less';
import { ISharePopupInstance, useSharePopup } from '@base/lib/share/useSharePopup';
import { observer, useStore } from 'base/store';
import { IUtmQuery } from 'base/utils/request';
import { getSharePopupTrackData, sharePopupModuleCode } from '@base/lib/share/utils';
import Tracker from '@base/lib/Tracker';
import { displayCtr, trackElement } from 'base/utils/tracker';
import { IClickShareItem, IGivenChannelType, IShareChannel } from '@base/lib/share/interface';
import { SHARE_CHANNELS_CONFIG } from '@base/lib/share/constant';
import { RouteEnum } from 'pro/config/routers';
import { useRouter } from 'next/router';
import { generatePath } from 'ava-util/dist/request';

export type ISharePopupPCProps = {
    className?: string;
    trigger?: string | string[];
    url?: string;
    size?: number;
    utmQuery?: IUtmQuery;
    trackTargetPrefix?: string; //Ctr 打点
    afterOpen?: () => void;
    onClickItem?: (shareChannel?: IClickShareItem) => void;
    shareUrls?: ISharePopupInstance;
    needLogin?: boolean;
};

type IShareChannelList = {
    className?: string;
    trackTargetPrefix?: string;
    shareUrls?: ISharePopupInstance;
    onClickItem?: (shareChannel?: IClickShareItem) => void;
    showName?: boolean;
} & ISharePopupPCProps

export const ShareChannelList: FC<IShareChannelList> = (props) => {
    const {
        className = '',
        trackTargetPrefix,
        onClickItem,
        showName = true,
        url: propsUrl,
        size = 32,
        utmQuery,
        needLogin = true,
        shareUrls: shareUrlsProps
    } = props

    const shareUrls = shareUrlsProps || useSharePopup({ url: propsUrl, utmQuery });

    const router = useRouter();
    const { userStore } = useStore();
    const { t } = useTrans();

    return (
        <div className={cn(className, 'text-nowrap')} data-t-mod={sharePopupModuleCode}>
            <div className={'g-list flex'}>
                {(['facebook', 'x'] as IGivenChannelType[]).map((channel) => {
                    const shareChannel = SHARE_CHANNELS_CONFIG.find(
                        (channelConfig) => channelConfig.channel === channel
                    )
                    if (!shareChannel) {
                        return ''
                    }
                    return (
                        <Tracker
                            key={channel}
                            trigger={'click'}
                            data={getSharePopupTrackData(shareChannel.track, trackTargetPrefix)}
                        >
                            <div className={'g-item'}>
                                <Image
                                    className={'g-icon'}
                                    src={shareChannel.imageUrl}
                                    width={size}
                                    fixed
                                    background={false}
                                    onClick={() => {
                                        if (needLogin && !userStore.checkAuth()) {
                                            router.push(generatePath(RouteEnum.LOGIN, { redirect: router.asPath }))
                                            return
                                        }
                                        const width = 930;
                                        const height = 680;
                                        const left = Math.round((getWindowWidth() - width) / 2);
                                        const top = Math.round((getWindowHeight() - height) / 2);
                                        onClickItem?.({channel})
                                        window.open(shareUrls?.[`${channel}Url`], shareChannel.channel, `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`)
                                    }}
                                />
                            </div>
                        </Tracker>
                    )
                })
                }
            </div>
        </div>
    )
}


export const PopupContent: FC<ISharePopupPCProps> = observer(props => {
    const {
        url: propsUrl,
        size = 32,
        utmQuery,
        trackTargetPrefix,
        onClickItem,
        needLogin = true,
        shareUrls: shareUrlsProps
    } = props;


    const shareUrls = shareUrlsProps || useSharePopup({ url: propsUrl, utmQuery });
    const { isLoading } = shareUrls
    const { t } = useTrans();

    return (
        <>
            <h3>{t('web_common_share_popup_title')}</h3>

            <Spin spinning={isLoading}>
                <ShareChannelList
                    className={styles['share-channel-list']}
                    onClickItem={onClickItem}
                    shareUrls={shareUrls}
                    {...props}
                />
            </Spin>

        </>
    )
});


export const SharePopupPC: FC<ISharePopupPCProps> = props => {
    const { className, children, trackTargetPrefix, afterOpen } = props;
    return (
        <Popover
            trigger={['click', 'click']}
            overlayClassName={cn(className, styles['share-pc'])}
            content={<PopupContent {...props}/>}
            placement={'bottom'}
            onOpenChange={open => {
                if (open) {
                    trackElement({
                        action: 'impr',
                        ...getSharePopupTrackData('SharePopup', trackTargetPrefix)
                    });
                    displayCtr(sharePopupModuleCode);
                    afterOpen?.();
                }
            }}
        >
            {children}
        </Popover>
    )
};
